import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig } from '@/auth/authConfig';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { initializeAppInsights } from '@/services/appInsights';
import { logger } from '@/services/logger';
import { env } from '@/env';
import App from './App';
import './index.css';

/**
 * Initialize Application Insights and the logger at module load, before the first render, so
 * errors thrown during the first render are reported and StrictMode's double-invoked effects
 * cannot initialize the SDK twice.
 */
const appInsights = initializeAppInsights(env.VITE_APPINSIGHTS_CONNECTION_STRING);
logger.initialize(appInsights);

if (appInsights) {
  logger.info('Application started', {
    environment: import.meta.env.MODE,
  });
}

/**
 * Initialize MSAL instance
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize React Query client
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Initialize and render the application with providers
 * Handle redirect promise for authentication flow
 */
void (async () => {
  // Initialize MSAL instance before using it
  await msalInstance.initialize();

  // Handle redirect promise to process authentication response
  await msalInstance.handleRedirectPromise();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
})();
